import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { i18n, RootState, setGlobalLoading, showMessage, useAppDispatch, useAppSelector, useRenewalOf } from 'src/common';
import { useGetOfferQuery, useHealthQuestionnaireQuestionsQuery, useSubmitHealthQuestionnaireMutation } from 'src/common/api/apollo/additional';
import { Flex, Loader, Text } from 'src/components';
import { HealthQuestionnaireFinishedCard } from 'src/components/cards/additional';
import { HealthQuestionnaireForm } from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.form';
import { buildInitialQuestionnaireValuesFromOffer } from 'src/components/forms/healthQuestionnaire/buildInitialQuestionnaireValuesFromOffer.util';
import type { AnyQuestion } from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.types';
import { colors } from 'src/theme';
import { AppRoutesAdditional } from '../../_router/app.routes';

const HealthQuestionnairePage = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const flow = useAppSelector((store: RootState) => store.state.flow);
  const soId = useAppSelector((store: RootState) => store.model.offer.soIdWeb);
  const { isRenewal } = useRenewalOf(soId);

  // Rule 8: a renewal goes back to ITS offers (the SO route), the regular funnel to the plain offers page.
  const backPath = (() => {
    if (flow.flowType === 'agent') return `${AppRoutesAdditional.AGENT_OFFERS_PREVIEW.fullPath}/${soId}`;
    if (isRenewal && soId) return `${AppRoutesAdditional.OFFERS_PREVIEW.fullPath}/${soId}`;
    return AppRoutesAdditional.OFFERS_PREVIEW.fullPath;
  })();

  const navigateBack = () => {
    navigate(backPath);
  }

  const { data: getOfferAdditionalData, loading: loadingOfferAdditional } = useGetOfferQuery({
    variables: { id: params.offerId || '' },
    skip: !params.offerId,
    onError: () => {
      navigateBack();
    }
  });

  const { data: getHealthQuestionnaireQuestionsData, loading: loadingHealthQuestionnaireQuestions } = useHealthQuestionnaireQuestionsQuery({
    variables: { brandCode: getOfferAdditionalData?.getOffer?.product?.brand?.code ?? '', productCode: getOfferAdditionalData?.getOffer?.product?.code ?? '' },
    skip: !getOfferAdditionalData?.getOffer?.product?.brand?.code || !getOfferAdditionalData?.getOffer?.product?.code,
    onError: () => {
      navigateBack();
    },
    onCompleted: (data) => {
      if (data.healthQuestionnaireQuestions.length === 0) {
        navigate(`${AppRoutesAdditional.OFFER.fullPath}/${params.offerId}`);
      }
    }
  });
  const [submitHealthQuestionnaire] = useSubmitHealthQuestionnaireMutation(
    {
      onCompleted: (data) => {
        if (data.submitHealthQuestionnaire.rejected) {
          navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE_REJECTED.fullPath}/${params.offerId}`);
        } else if (data.submitHealthQuestionnaire.needsConfirmation) {
          navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE_NEEDS_CONFIRMATION.fullPath}/${params.offerId}`);
        } else {
          navigate(`${AppRoutesAdditional.OFFER.fullPath}/${params.offerId}`);
        }
        dispatch(setGlobalLoading(false));
      },
      onError: () => {
        dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 5000 }));
        dispatch(setGlobalLoading(false));
      }
    }
  );

  const questionsList = getHealthQuestionnaireQuestionsData?.healthQuestionnaireQuestions;

  const initialValues = useMemo(
    () =>
      buildInitialQuestionnaireValuesFromOffer(
        (questionsList ?? []) as AnyQuestion[],
        getOfferAdditionalData?.getOffer?.productTypeSpecific?.healthQuestionnaire ?? null,
      ),
    [questionsList, getOfferAdditionalData?.getOffer?.productTypeSpecific?.healthQuestionnaire],
  );

  if (loadingOfferAdditional || loadingHealthQuestionnaireQuestions) return <Loader page />;
  if (!getOfferAdditionalData || !getHealthQuestionnaireQuestionsData) return null;

  const { getOffer: offer } = getOfferAdditionalData;


  const handleConfirm = () => {
    submitHealthQuestionnaire({ variables: { id: params.offerId || '', flow: flow.flowType ?? 'web' } });
  };

  if (getOfferAdditionalData.getOffer?.productTypeSpecific?.healthQuestionnaire?.completedAt) {
    return <HealthQuestionnaireFinishedCard healthQuestionnaire={getOfferAdditionalData.getOffer?.productTypeSpecific?.healthQuestionnaire} offerId={params.offerId || ''} 
      isConfirmed={getOfferAdditionalData.getOffer?.productTypeSpecific?.healthQuestionnaire?.isConfirmed ?? false} />;
  }

  return (
    <Flex vertical gap={24}>
      <Text type="xLarge" bold>
        {i18n.t('pages.healthQuestionnaire.pageTitle', { brandName: offer?.product?.brand?.name ?? offer?.product?.name ?? '' })}
      </Text>
      <Text type="small" color={colors.text3}>
        {i18n.t('pages.healthQuestionnaire.pageDescription')}
      </Text>

      <HealthQuestionnaireForm
        questions={getHealthQuestionnaireQuestionsData.healthQuestionnaireQuestions}
        initialValues={initialValues}
        onConfirm={handleConfirm}
        backPath={backPath}
        autoSave={
          params.offerId && offer?.product?.brand?.code
            ? { offerId: params.offerId, brandCode: offer.product.brand.code }
            : undefined
        }
      />
    </Flex>
  );
};

export default HealthQuestionnairePage;